/* Hallmark · component: sticky voucher dock · genre: playful · theme: project tokens (orange --primary)
 * layout: stacked at every width — offer pane over code stub, horizontal perforation
 * anchor: mobile ticket above scroll-to-top + bottom sheet · desktop middle of the left edge
 * collapsed chip: the same ticket made small — masked notches on the top/bottom edges, a
 *   vertical perforation, and a torn-off white stub carrying the count (or the glyph when
 *   there is only one voucher). Trigger and panel are one object at two sizes.
 * states: default · hover · focus · active · hidden. disabled/loading/error/success don't
 *   apply — the dock renders nothing while loading, and copy feedback lives on the ticket.
 * contrast: chip label is ink-on-orange (text-foreground) 4.95:1, stub numeral is
 *   --primary-ink on white 4.6:1 — both clear AA
 * motion: 10s auto-rotate, running whether the dock is open or collapsed, so the chip's
 *   label and the panel's card are always the same voucher. Open, the swap is a carousel
 *   track (420ms ease-out, outgoing card exits left as the incoming arrives from the right,
 *   both on screen); slides are flex-stretched to the tallest card, so the dock never
 *   resizes between tickets despite their height delta (4.75rem vs 7.5rem stub, 0–3
 *   description lines), and a trailing clone of the first voucher keeps the wrap travelling
 *   left instead of rewinding. Collapsed, there is nothing on screen to travel, so the track
 *   jumps and only the chip label crossfades (300ms opacity).
 *   pauses on hover · focus · touch · hidden tab · non-idle copy; instant under reduced motion
 * pre-emit critique: P5 H5 E5 S5 R5 V5
 * The ticket itself lives in `@/components/common/voucher-ticket` — this file owns only
 * the dock behaviour around it (rotation, clipboard, collapse memory, focus handoff).
 */
import SiteInertiaController from '@/actions/App/Http/Controllers/SiteInertiaController';
import VoucherDetailInertiaController from '@/actions/App/Http/Controllers/VoucherDetailInertiaController';
import { VoucherTicket, type VoucherCopyState } from '@/components/common/voucher-ticket';
import { useAvailableVouchers } from '@/hooks/use-available-vouchers';
import { cn } from '@/lib/utils';
import { Ticket } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

const ROTATE_INTERVAL = 10_000;
/** Keep in sync with the `duration-[420ms]` on the track below. */
const TRACK_MS = 420;
const COPY_FEEDBACK_MS = 2_000;
const ERROR_FEEDBACK_MS = 5_000;
const COLLAPSED_KEY = 'voucher-dock:collapsed';

type CopyState = VoucherCopyState;

export function StickyVoucher() {
  const { vouchers, isLoading } = useAvailableVouchers();
  const panelId = useId();

  const [isOpen, setIsOpen] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);
  // Position along the track, not the voucher index — the last position is a clone of
  // the first voucher. `currentIndex` below maps it back.
  const [slide, setSlide] = useState(0);
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [isHovered, setIsHovered] = useState(false);
  // Tracked apart from `isHovered`: the panel's own hover state can't be trusted while
  // the panel is collapsed, and the chip is the only thing on screen to hover then.
  const [isChipHovered, setIsChipHovered] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const [isTabHidden, setIsTabHidden] = useState(false);
  const [isSnapping, setIsSnapping] = useState(false);

  const timers = useRef<number[]>([]);
  // The track's own timer and frame live outside `timers`: clearTimers() runs on copy
  // and on manual paging, and clearing the clone-settle mid-flight would strand the
  // track on the clone with no way back to a real slide.
  const swapTimer = useRef<number | null>(null);
  const swapFrame = useRef<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const hasMany = vouchers.length > 1;
  // A trailing clone of the first voucher. Without it the wrap from the last voucher
  // back to the first would rewind the track backwards past everything in between.
  const slides = hasMany ? [...vouchers, vouchers[0]] : vouchers;
  const lastSlide = slides.length - 1;
  const currentIndex = hasMany ? slide % vouchers.length : 0;

  const clearTimers = useCallback(() => {
    timers.current.forEach(window.clearTimeout);
    timers.current = [];
  }, []);

  const defer = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  const clearSwap = useCallback(() => {
    if (swapTimer.current !== null) {
      window.clearTimeout(swapTimer.current);
      swapTimer.current = null;
    }
    if (swapFrame.current !== null) {
      window.cancelAnimationFrame(swapFrame.current);
      swapFrame.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearTimers();
      clearSwap();
    };
  }, [clearTimers, clearSwap]);

  /**
   * Move the track with no travel, then hand the transition back. Pass `then` to
   * animate onward from the snapped position — that is how both wrap directions
   * re-enter the track at an equivalent slide before travelling for real.
   */
  const snapTo = useCallback(
    (target: number, then?: number) => {
      clearSwap();
      setIsSnapping(true);
      setSlide(target);

      // Two frames, not one: the first lets the snap paint. Releasing inside a single
      // frame batches both into one paint and the browser drops the transition entirely.
      swapFrame.current = window.requestAnimationFrame(() => {
        swapFrame.current = window.requestAnimationFrame(() => {
          swapFrame.current = null;
          setIsSnapping(false);
          if (then !== undefined) {
            setSlide(then);
          }
        });
      });
    },
    [clearSwap],
  );

  const advance = useCallback(() => {
    if (!hasMany) {
      return;
    }
    clearSwap();

    // Two ways to arrive without the travel, both landing on a real slide rather than
    // the clone: nobody can see the track while the dock is collapsed, so animating it
    // would be work for an invisible, inert element; and under reduced motion the
    // rotation still runs — it carries information, not decoration — just not the slide.
    if (!isOpen || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      snapTo((currentIndex + 1) % vouchers.length);
      return;
    }

    if (slide >= lastSlide) {
      // Sitting on the clone because the settle was interrupted: re-enter at the real
      // first slide, then travel on from there.
      snapTo(0, 1);
      return;
    }

    const next = slide + 1;
    setIsSnapping(false);
    setSlide(next);

    if (next === lastSlide) {
      // Landed on the clone. It shows the same voucher as slide 0, so exchanging them
      // once the travel finishes is invisible — and leaves the next advance heading
      // left again instead of rewinding through everything in between.
      swapTimer.current = window.setTimeout(() => {
        swapTimer.current = null;
        snapTo(0);
      }, TRACK_MS);
    }
  }, [clearSwap, currentIndex, hasMany, isOpen, lastSlide, slide, snapTo, vouchers.length]);

  const retreat = useCallback(() => {
    if (!hasMany) {
      return;
    }
    clearSwap();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      snapTo((currentIndex - 1 + vouchers.length) % vouchers.length);
      return;
    }

    if (slide === 0) {
      // Enter from the clone — same voucher, same pixels — then travel right, so the
      // previous card arrives from the left.
      snapTo(lastSlide, lastSlide - 1);
      return;
    }

    setIsSnapping(false);
    setSlide(slide - 1);
  }, [clearSwap, currentIndex, hasMany, lastSlide, slide, snapTo, vouchers.length]);

  // Restore the visitor's own choice for this session instead of re-expanding
  // on every page view.
  useEffect(() => {
    if (vouchers.length === 0) {
      return;
    }
    setIsOpen(window.sessionStorage.getItem(COLLAPSED_KEY) !== '1');
    setHasEntered(true);
  }, [vouchers.length]);

  // Rotation pauses while the visitor is reading, and while the tab is in the
  // background — the code must never swap out mid-copy.
  useEffect(() => {
    const onVisibilityChange = () => setIsTabHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Whichever half is on screen owns the pause: the panel's hover/touch while open, the
  // chip's while collapsed. A label swapping under a pointer that is about to click it is
  // the same discourtesy as a card swapping mid-read.
  const isPaused = isTabHidden || copyState !== 'idle' || (isOpen ? isHovered || isTouching : isChipHovered);

  useEffect(() => {
    // No `isOpen` guard — rotation runs collapsed too, which is what keeps the chip's
    // label and the panel's card on the same voucher whenever the dock is opened.
    if (isPaused || !hasMany) {
      return;
    }
    // A timeout, not an interval: `advance` changes identity with every slide, so this
    // effect re-runs on each one and every voucher gets a fresh full dwell — including
    // after the visitor pages manually, which would otherwise be followed by an
    // auto-advance a moment later.
    const tick = window.setTimeout(advance, ROTATE_INTERVAL);

    return () => window.clearTimeout(tick);
  }, [isPaused, hasMany, advance]);

  // Whichever half is hidden becomes inert, so focus has to be handed to the
  // half that stays — otherwise a keyboard visitor is dropped back to <body>.
  const setOpen = useCallback((next: boolean) => {
    const active = document.activeElement;
    const wasFocusInside = next ? chipRef.current === active : Boolean(panelRef.current?.contains(active));

    setIsOpen(next);
    window.sessionStorage.setItem(COLLAPSED_KEY, next ? '0' : '1');

    if (wasFocusInside) {
      requestAnimationFrame(() => (next ? closeRef : chipRef).current?.focus());
    }
  }, []);

  const goTo = useCallback(
    (delta: number) => {
      clearTimers();
      setCopyState('idle');
      // Same track, same motion — paging never looks like a different mechanism than
      // the rotation it interrupts.
      if (delta > 0) {
        advance();
      } else {
        retreat();
      }
    },
    [advance, clearTimers, retreat],
  );

  const handleCopy = useCallback(
    async (code: string) => {
      clearTimers();
      setCopyState('copying');

      try {
        await navigator.clipboard.writeText(code);
        setCopyState('copied');
        // Task complete — step out of the way, and remember that for the session.
        defer(() => {
          setCopyState('idle');
          setOpen(false);
        }, COPY_FEEDBACK_MS);
      } catch (error) {
        console.error('Failed to copy voucher code:', error);
        setCopyState('error');
        defer(() => setCopyState('idle'), ERROR_FEEDBACK_MS);
      }
    },
    [clearTimers, defer, setOpen],
  );

  const currentVoucher = vouchers[currentIndex];

  if (isLoading || vouchers.length === 0 || !currentVoucher) {
    return null;
  }

  const labelFor = (voucher: (typeof vouchers)[number]) => (voucher.discount_label ? `Giảm ${voucher.discount_label}` : 'Ưu đãi');
  const chipLabel = labelFor(currentVoucher);

  // A site-attached voucher goes straight to booking; everything else goes to its
  // detail page, which is the only surface that can explain a rate- or service-scoped
  // code. Before that page existed these vouchers had no destination at all.
  // `site` alone, no `date`: the search page applies its own "after 16:00 → tomorrow"
  // rule, which is a better default than pinning today from the client.
  const ctaFor = (voucher: (typeof vouchers)[number]) => {
    const target = voucher.target;

    // Inertia keeps this component mounted across the navigation, so the dock would
    // otherwise sit open on top of the page it just sent you to.
    const onNavigate = () => setOpen(false);

    if (target?.type === 'booking_site') {
      return {
        href: SiteInertiaController.search({ query: { site: target.mapping_code } }).url,
        label: target.name ? `Đặt vé tại ${target.name}` : 'Đặt vé ngay',
        onNavigate,
      };
    }

    return {
      href: VoucherDetailInertiaController.show(voucher.code).url,
      label: 'Xem chi tiết ưu đãi',
      onNavigate,
    };
  };

  return (
    <>
      {/* Collapsed chip — a miniature of the ticket it opens. Mobile stacks it above
          the scroll-to-top control (48px at right-6/bottom-6, so its top edge is 72px)
          on the same right rail, 12px of air between them; desktop anchors it to the
          middle of the left edge, where the panel also opens from. The wrapper owns
          position so the button keeps its own motion transforms.

          The nesting is load-bearing: a mask clips everything outside the border box,
          box-shadow included. So the ring and the transforms live on the unmasked
          button, the drop-shadow on a wrapper (filter is applied before the mask), and
          only the innermost span carries the punched silhouette. */}
      <div className="pointer-events-none fixed right-6 bottom-21 z-50 w-fit sm:top-1/2 sm:right-auto sm:bottom-auto sm:left-6 sm:-translate-y-1/2">
        {/* Masked like the chip, so the ping ripples out as a ticket rather than a
            circle behind a shape that isn't one. */}
        {!isOpen && <span aria-hidden="true" className="voucher-chip voucher-chip-halo absolute inset-0 rounded-xl" />}

        <button
          ref={chipRef}
          type="button"
          onClick={() => setOpen(true)}
          // Hold the rotation while the visitor is pointed at the chip or tabbed onto it.
          onMouseEnter={() => setIsChipHovered(true)}
          onMouseLeave={() => setIsChipHovered(false)}
          onFocus={() => setIsChipHovered(true)}
          onBlur={() => setIsChipHovered(false)}
          aria-expanded={isOpen}
          aria-controls={panelId}
          inert={isOpen}
          className={cn(
            'voucher-chip-trigger group relative block cursor-pointer rounded-xl',
            // The ring is a box-shadow and must appear instantly — it is deliberately
            // absent from the transition list below.
            'focus-visible:ring-primary-ink/60 focus-visible:ring-[6px] focus-visible:outline-none',
            'motion-safe:transition-[transform,translate,opacity] motion-safe:duration-1000 motion-safe:ease-out',
            'motion-safe:hover:-translate-y-0.5 motion-safe:active:translate-y-0 motion-safe:active:scale-[0.98]',
            isOpen || !hasEntered ? 'pointer-events-none translate-y-3 opacity-0' : 'pointer-events-auto translate-y-0 opacity-100',
            !isOpen && hasEntered && 'voucher-chip-shake',
          )}
        >
          {/* Hidden from assistive tech so the accessible name is one clean sentence
              instead of "Giảm 30% 3" — the sr-only line below carries both facts. */}
          <span aria-hidden="true" className="voucher-chip-shadow block">
            <span className="voucher-chip bg-primary flex h-11 items-stretch overflow-hidden rounded-xl sm:h-12">
              {/* Ink on orange, not white on orange: the brand orange is light enough
                  that white lands at 3.10:1, while near-black reaches 4.95:1 — so the
                  accessible pairing is also the louder one. */}
              <span className="text-primary-foreground flex min-w-0 flex-1 items-center px-3 text-[13px] leading-none font-extrabold tracking-tight transition-colors sm:px-4 sm:text-sm">
                {/* Every label is mounted in the same grid cell and crossfaded by opacity,
                    so the swap needs no timer, no keyed remount and no second copy of the
                    text — and the cell sizes to the widest label, so the chip holds one
                    width instead of jittering as the rotation moves through them.
                    Capped: the dock is fixed-position, so an unusually long label
                    (`Giảm 50.000đ`) would otherwise grow the chip across the viewport. */}
                <span className="grid max-w-[8.5rem] sm:max-w-[11rem]">
                  {vouchers.map((voucher, index) => (
                    <span
                      key={voucher.id}
                      className={cn(
                        'col-start-1 row-start-1 truncate',
                        'motion-safe:transition-opacity motion-safe:duration-300 motion-safe:ease-out',
                        index === currentIndex ? 'opacity-100' : 'opacity-0',
                      )}
                    >
                      {labelFor(voucher)}
                    </span>
                  ))}
                </span>
              </span>

              {/* The torn-off half. It carries the count when there are several
                  vouchers, and the glyph when there is only one, so the stub is never
                  an empty white tab. */}
              <span className="voucher-chip-stub text-primary-foreground grid shrink-0 place-items-center font-bold tabular-nums">
                {hasMany ? vouchers.length : <Ticket className="size-4" />}
              </span>
            </span>
          </span>

          <span className="sr-only">{hasMany ? `${chipLabel} — ${vouchers.length} ưu đãi` : chipLabel}</span>
        </button>
      </div>

      {/* Expanded ticket. Middle of the left edge on desktop, opening from the same
          anchor as the chip; an inset sheet on mobile, lifted clear of the
          scroll-to-top button. Position lives here, motion on the child, so the
          centring translate and the enter transform never fight. */}
      <div
        ref={panelRef}
        id={panelId}
        inert={!isOpen}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocusCapture={() => setIsHovered(true)}
        onBlurCapture={() => setIsHovered(false)}
        // Touch has no hover to pause on, so the finger itself is the pause. Mouse is
        // excluded — it already pauses via hover, and doubling up would fight it.
        onPointerDown={(event) => {
          if (event.pointerType !== 'mouse') {
            setIsTouching(true);
          }
        }}
        // pointerup alone misses the finger that slides off the panel before lifting.
        onPointerUp={() => setIsTouching(false)}
        onPointerCancel={() => setIsTouching(false)}
        onPointerLeave={() => setIsTouching(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
        className={cn(
          // 22rem sits in the homepage's voucher-card range (260–300px artwork plus
          // chrome). Stacked, the offer pane owns the whole width, so the card no
          // longer has to be wide to give the copy room.
          'fixed inset-x-2 bottom-20 z-50 sm:inset-x-auto sm:top-1/2 sm:bottom-auto sm:left-6 sm:w-[22rem] sm:-translate-y-1/2',
        )}
      >
        <div
          className={cn(
            'motion-safe:transition-[transform,opacity] motion-safe:duration-300 motion-safe:ease-out',
            isOpen && hasEntered ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-4 opacity-0',
          )}
        >
          {/* `overflow-x-clip`, not `overflow-hidden`: clip is the one that leaves the
              other axis `visible`, so the card's downward drop shadow still escapes
              while the travel is contained. hidden would force overflow-y to auto,
              killing the shadow and risking a scroll container. The clip lands exactly
              on the panel box, which is also what keeps a rightward translate from
              creating real horizontal scroll on the inset-x-3 mobile panel. */}
          <div className="overflow-x-clip">
            <div
              className={cn('flex', isSnapping ? 'transition-none' : 'transition-transform duration-[420ms] ease-out')}
              // Computed, so it cannot be a Tailwind class. Slides are w-full of the
              // track, so one step is exactly one card.
              style={{ transform: `translateX(-${slide * 100}%)` }}
            >
              {slides.map((voucher, index) => {
                const isActive = index === slide;

                return (
                  // Flex stretch sizes every slide to the tallest card, so the dock
                  // never resizes between vouchers — and `inert` keeps the off-screen
                  // ones out of the tab order entirely.
                  <div key={`${voucher.id}-${index}`} className="w-full shrink-0" inert={!isActive}>
                    <VoucherTicket
                      voucher={voucher}
                      className="h-full"
                      copyState={isActive ? copyState : 'idle'}
                      onCopy={handleCopy}
                      close={{
                        onClose: () => setOpen(false),
                        // Only the visible card holds the ref, so the collapse focus
                        // handoff never targets a card parked off-screen.
                        ref: isActive ? closeRef : undefined,
                        controls: panelId,
                        expanded: isOpen,
                      }}
                      pager={hasMany ? { index: currentIndex, total: vouchers.length, onPrev: () => goTo(-1), onNext: () => goTo(1) } : undefined}
                      cta={ctaFor(voucher)}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Outside the track — the announcement must not travel with the cards, and a
              slide is deliberately not announced: every 10s would be noise, and a
              screen-reader visitor focused inside has rotation paused anyway. */}
          <span aria-live="polite" className="sr-only">
            {copyState === 'copied' ? `Đã sao chép mã ${currentVoucher.code}` : ''}
            {copyState === 'error' ? 'Không sao chép được. Hãy chạm giữ vào mã để chọn và sao chép thủ công.' : ''}
          </span>
        </div>
      </div>
    </>
  );
}
